import {
    Card,
    Col,
    Row,
} from 'antd';
import { groupBy, map, orderBy, sumBy } from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { Dispatch } from 'redux';
import { AppState, DashboardSearch, DashboardState, StateDetailState, TownshipData } from 'src/store';
import { actions as statesActions } from '../../states/redux';
import MCILForTownships from '../components/MCILForTownships';
import { actions } from '../redux';

const mapStateToProps = ({ fdiDashboardState, stateDetailState }: AppState, ownProps: P) => {
    // const error = fdiDashboardState.status === 'error' ? fdiDashboardState.error : null;
    const stateName = ownProps.match.params.stateName ? ownProps.match.params.stateName : null;

    return {
        stateName,
        stateDetailState,
        fdiDashboardState,
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        onLoad: (search: DashboardSearch) => {
            if (search.stateName) {
                dispatch(statesActions.fetchStateRequest(search.stateName));
            }
            dispatch(actions.fetchRequest(search));
        },
    };
};

const capitalFormatter = (value: string) => `K${Number(value).toFixed(2)}MM`;

interface FiscalYearItem {
    fiscalYear: string;
    capital: number;
}
interface SectorItem {
    sector: string;
    capital: number;
}
interface P extends RouteComponentProps<{ stateName: string }> {
    stateName: string | null;
    fdiDashboardState: DashboardState;
    stateDetailState: StateDetailState;
    onLoad: (search: DashboardSearch) => void;
}
interface S {
    fiscalYears: FiscalYearItem[];
    sectors: SectorItem[];
    townships: TownshipData[];
    selectedFiscalYearIndex?: number;
    selectedSectorIndex?: number;
}
class MCILDashboard extends React.Component<P, S> {
    constructor(props: P) {
        super(props);
        this.state = { fiscalYears: [], sectors: [], townships: [] };
    }

    componentDidMount() {
        const { onLoad, stateName } = this.props;
        const search: DashboardSearch = { stateName, typeOfInvestment: 'MCIL' }
        onLoad(search);
    }

    componentWillReceiveProps(nextProps: P) {
        if (this.props.fdiDashboardState.status !== nextProps.fdiDashboardState.status) {
            const fiscalYears = this.getFiscalYears(nextProps);
            const sectors = this.getSectors(nextProps);
            const townships = this.getTownships(nextProps);
            this.setState({ fiscalYears, sectors, townships });
        }
    }

    getFiscalYears = (props: P) => {
        const { fdiDashboardState } = props;
        if (fdiDashboardState.status === 'loaded') {
            const grp = groupBy(fdiDashboardState.investments, inv => inv.financialYear);
            const result = map(grp, (v, k) => ({ fiscalYear: k, capital: sumBy(v, i => i.capital) }));
            return result.filter(i => i.capital > 0);
        }

        return [];
    }

    handleFiscalYearClick = (data: FiscalYearItem[], index: number) => {
        const { selectedFiscalYearIndex, fiscalYears } = this.state;
        if (selectedFiscalYearIndex === index) {
            const sectors = this.getSectors(this.props);
            const townships = this.getTownships(this.props);
            this.setState({
                selectedFiscalYearIndex: undefined,
                selectedSectorIndex: undefined,
                sectors,
                townships,
            });
        } else {
            const fiscalYear = fiscalYears[index].fiscalYear;
            const sectors = this.getSectors(this.props, fiscalYear);
            const townships = this.getTownships(this.props, fiscalYear);
            this.setState({
                selectedFiscalYearIndex: index,
                selectedSectorIndex: undefined,
                sectors,
                townships,
            });
        }
    }

    handleSectorClick = (data: SectorItem[], index: number) => {
        const { selectedFiscalYearIndex, selectedSectorIndex, fiscalYears, sectors } = this.state;
        const fiscalYear = selectedFiscalYearIndex ?
            fiscalYears[selectedFiscalYearIndex].fiscalYear : undefined;
        if (selectedSectorIndex === index) {
            const townships = this.getTownships(this.props, fiscalYear);
            this.setState({
                selectedSectorIndex: undefined,
                townships,
            });
        } else {
            const sector = sectors[index].sector;
            const townships = this.getTownships(this.props, fiscalYear, sector);
            this.setState({
                selectedSectorIndex: index,
                townships,
            });
        }
    }

    getSectors = (props: P, fiscalYear?: string) => {
        const { fdiDashboardState } = props;
        if (fdiDashboardState.status === 'loaded') {
            const investments = fiscalYear ?
                fdiDashboardState.investments.filter(i => i.financialYear === fiscalYear) :
                fdiDashboardState.investments;
            const grp = groupBy(investments, inv => inv.sector);
            const result = map(grp, (v, k) => ({ sector: k, capital: sumBy(v, i => i.capital) }));
            return orderBy(result.filter(i => i.capital > 0), i => i.capital, 'desc');
        }

        return [];
    }

    getTownships = (props: P, fiscalYear?: string, sector?: string) => {
        const { fdiDashboardState } = props;
        if (fdiDashboardState.status === 'loaded') {
            let investments = fiscalYear ?
                fdiDashboardState.investments.filter(i => i.financialYear === fiscalYear) :
                fdiDashboardState.investments;
            investments = sector ?
                investments.filter(i => i.sector === sector) : investments;
            const grp = groupBy(investments, inv => inv.township);
            const result = map(grp, (v, k) => ({
                stateCode: v[0].stateCode,
                state: v[0].state,
                township: k,
                townshipCode: v[0].townshipCode,
                capital: sumBy(v, i => i.capital)
            } as TownshipData));
            return orderBy(result.filter(i => i.capital > 0), i => i.capital, 'desc');
        }

        return [];
    }

    render() {
        const { fdiDashboardState, stateDetailState, stateName } = this.props;
        const {
            fiscalYears,
            sectors,
            townships,
            selectedFiscalYearIndex,
            selectedSectorIndex,
        } = this.state;
        const loading = (fdiDashboardState.status !== 'loaded');
        const stateCode = stateName ? (stateDetailState.status === 'loaded' ? stateDetailState.data.code : undefined) : undefined;
        return (
            <div>
                <Row>
                    <Col>
                        <Card title="Fiscal Year" loading={loading}>
                            <ResponsiveContainer width="100%" height={120}>
                                <BarChart data={fiscalYears} margin={{ top: 20, right: 20, left: 20, bottom: 5 }}>
                                    <XAxis dataKey="fiscalYear" tickLine={false} />
                                    <Bar dataKey="capital" fill="#8884d8" onClick={this.handleFiscalYearClick}>
                                        <LabelList dataKey="capital" position="top" formatter={capitalFormatter} />
                                        {fiscalYears.map((fy, index) => (
                                            <Cell
                                                cursor="pointer"
                                                fill={index === selectedFiscalYearIndex ? '#82ca9d' : '#8884d8'}
                                                key={`cell-${index}`}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Card title="Sectors" loading={loading}>
                            <ResponsiveContainer width="100%" height={865}>
                                <BarChart
                                    layout="vertical"
                                    data={sectors}
                                    margin={{ top: 20, right: 80, left: 20, bottom: 5 }}
                                >
                                    <XAxis type="number" />
                                    <YAxis type="category" dataKey="sector" tickLine={false} width={150} />
                                    <Bar dataKey="capital" fill="#8884d8" onClick={this.handleSectorClick}>
                                        <LabelList dataKey="capital" position="right" formatter={capitalFormatter} />
                                        {sectors.map((s, index) => (
                                            <Cell
                                                cursor="pointer"
                                                fill={index === selectedSectorIndex ? '#82ca9d' : '#8884d8'}
                                                key={`cell-${index}`}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <MCILForTownships stateCode={stateCode} loading={loading} townships={townships} />
                    </Col>
                </Row>
            </div>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MCILDashboard);



// WEBPACK FOOTER //
// ./src/Dashboard/containers/MCILDashboard.tsx